import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import classNames from 'classnames/bind';

import SocialPlugin from '@/components/SocialPlugin';

import styles from './BrandMallHeader.module.scss';

const cx = classNames.bind(styles);

const allSections = [
  { label: '홈', id: 'section-home' },
  { label: '딱! 2주만 특가', id: 'section-two-week' },
  { label: '럭셔리', id: 'section-luxury' },
  { label: '기업복지', id: 'section-welfare-best' },
  { label: '베스트', id: 'section-best' },
  { label: '쿠폰혜택', id: 'section-coupon' },
];

// BrandMall에서 사용하는 Agent 타입 (getServerSideProps에서 전달되는 필수 필드들)
interface BrandMallAgent {
  agtCode: string;
  brandMallActive: boolean;
  landingUrl: string;
  name: string;
  luxuryDesc?: string;
}

interface BrandMallHeaderProps {
  agent?: BrandMallAgent;
}

const BrandMallHeader = ({ agent }: BrandMallHeaderProps) => {
  const router = useRouter();
  const { agtName } = router.query;

  const [currentUrl, setCurrentUrl] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);

  const [sections, setSections] = useState([allSections[0]]); // 홈만 초기 표시

  const isSupportBrandLogo = () => {
    const supportedAgtNames = ['lottetour', 'onlinetour', 'ybtour', 'modetour', 'verygoodtour', 'krt'];
    return supportedAgtNames.includes(agtName as string);
  };

  // 로고 이미지 URL 생성
  const logoUrl = useMemo(() => {
    if (isSupportBrandLogo() && agtName) {
      const imageName = `BrandMallHeader_ico_${agtName}.png`;
      return `https://techminds.pstatic.net/pkgtour/brandMall/${imageName}`;
    } else if (agent?.agtCode) {
      return `https://techminds.pstatic.net/pkgtour/statics/agents/${agent.agtCode}.png`;
    }
    return undefined;
  }, [agtName, agent?.agtCode]);

  const updateVisibleSections = useCallback(() => {
    const visibleSections = allSections.filter((section) => {
      if (section.id === 'section-home') return true;
      const element = document.getElementById(section.id);
      return element !== null;
    });

    // 기존 섹션 수와 다를 때만 업데이트
    if (
      visibleSections.length !== sections.length ||
      !visibleSections.every((section, index) => sections[index]?.id === section.id)
    ) {
      setSections(visibleSections);
    }
  }, [sections]);

  // 활성화된 탭을 화면에 보이도록 스크롤(가로 사이즈 좁은 경우)
  const scrollToActiveTab = (index: number) => {
    if (!navRef.current) return;

    const navContainer = navRef.current;
    const activeTab = navContainer.children[index] as HTMLElement;

    if (!activeTab) return;

    const containerRect = navContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    const isTabOutOfView = tabRect.left < containerRect.left || tabRect.right > containerRect.right;

    if (isTabOutOfView) {
      const scrollLeft = activeTab.offsetLeft - containerRect.width / 2 + tabRect.width / 2;
      navContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleMenuClick = (index: number, sectionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    isScrollingRef.current = true;

    setCurrentIndex(index);
    scrollToActiveTab(index);

    requestAnimationFrame(() => {
      if (sectionId === 'section-home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const el = document.getElementById(sectionId);
        if (el) {
          const rect = el.getBoundingClientRect();
          const scrollTop = window.scrollY;
          const targetPosition = rect.top + scrollTop - 108; // 10px 위에 위치하도록 조정

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
          });
        }
      }
    });

    setTimeout(() => {
      isScrollingRef.current = false;
    }, 600);
  };

  useEffect(() => {
    if (agtName) {
      setCurrentUrl(`${window.location.origin}/brandmall/${agtName}`);
    }
  }, [agtName]);

  useEffect(() => {
    scrollToActiveTab(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    const checkTimer = setTimeout(() => {
      updateVisibleSections();
    }, 1000);

    const intervalId = setInterval(() => {
      updateVisibleSections();
    }, 2000);

    return () => {
      clearTimeout(checkTimer);
      clearInterval(intervalId);
    };
  }, [updateVisibleSections]);

  useEffect(() => {
    let requestId: number | null = null;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      if (requestId !== null) return;

      requestId = requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const windowHeight = window.innerHeight;
        const offset = windowHeight * 0.3; // 30% 지점에서 활성화

        // 현재 활성 섹션 결정
        const activeIndex =
          scrollTop < 100
            ? 0
            : sections.findLastIndex((section: { label: string; id: string }) => {
                const element = document.getElementById(section.id);
                if (!element) return false;

                const rect = element.getBoundingClientRect();
                const elementTop = rect.top + scrollTop;

                return scrollTop + offset >= elementTop;
              }) ?? 0;

        setCurrentIndex(activeIndex);
        requestId = null;
      });
    };

    // 초기 설정을 위해 약간의 지연 후 실행
    const initTimer = setTimeout(() => {
      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 500);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('scroll', handleScroll);
      if (requestId !== null) {
        cancelAnimationFrame(requestId);
      }
    };
  }, [sections]);

  return (
    <div className={cx('BrandMallHeader')} data-service="pkgtour">
      <div className={cx('inner')}>
        <nav className={cx('top')}>
          <div className={cx('inner')}>
            <a href="https://www.naver.com/" className={cx('naver')}>
              <span className={cx('blind')}>네이버</span>
            </a>
            <a href="https://pkgtour.naver.com/" className={cx('packages')}>
              <span className={cx('blind')}>패키지</span>
            </a>
            <a
              href="#"
              className={cx('brand')}
              data-type={agtName}
              onClick={(e) => {
                e.preventDefault();
                window.location.href = currentUrl;
              }}
            >
              {logoUrl && <img className={cx('logo')} src={logoUrl} alt={agtName as string} />}
              {/* <span className={cx('blind')}>{agtName}</span> */}
              <i className={cx('beta')}>
                <span className={cx('blind')}>beta</span>
              </i>
            </a>
            {currentUrl && (
              <SocialPlugin data-title={`${agtName} 브랜드관`} data-url={currentUrl}>
                <button className={cx('share')}>
                  <span className={cx('blind')}>공유</span>
                </button>
              </SocialPlugin>
            )}
          </div>
        </nav>
        <nav className={cx('nav')}>
          <div className={cx('inner')} ref={navRef}>
            {sections.map((section, index) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={cx('anchor')}
                aria-current={currentIndex === index ? 'true' : 'false'}
                onClick={(e) => handleMenuClick(index, section.id, e)}
              >
                <span className={cx('txt')}>{section.label}</span>
              </a>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default BrandMallHeader;
